import { useMemo, useEffect } from 'react';
import { useFormContext, Controller, FieldArrayWithId } from 'react-hook-form';
import { skipToken } from '@reduxjs/toolkit/query';

import useCooperativeId from 'shared/hooks/useCooperativeId';
import useSelector from 'shared/hooks/useSelector';
import useTranslation from 'shared/hooks/useTranslation';

import type { Option } from 'shared/models/common';
import getSelectHelpers from 'shared/utils/getSelectHelpers';

import { userSpaceRenovationRequestsApi } from '../../../api';

import {
  ValidationWarningMessage,
  HTJ_STRING_WITH_LINE_BREAKS_PATTERN,
} from '../constants';
import type {
  FormState,
  RenovationTypeOption,
} from '../SpaceRenovationRequestCreateForm.type';

import renovationExecutorFactory from './renovationExecutorFactory';

import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Autocomplete from 'shared/components/Autocomplete';
import ControlLabel from 'shared/components/ControlLabel';
import Select from 'shared/components/Select';
import Textarea from 'shared/components/Textarea';
import ValidationWarning from 'components/validation-warning';
import { RenovationExecutor } from 'shared/constants/space-renovation-requests';

type RenovationTypeProps = {
  descriptionsMaxLength: number;
  foreignRegistrationNumberMaxLength: number;
  organizationNameMaxLength: number;
  field: FieldArrayWithId<FormState, 'renovationTypes', 'id'>;
  idx: number;
  executorCodes: Option<number>[];
  renovationTypeOptions: RenovationTypeOption[];
  totalLength: number;
  remove: (idx: number) => void;
};

const RenovationType = ({
  descriptionsMaxLength,
  foreignRegistrationNumberMaxLength,
  organizationNameMaxLength,
  executorCodes,
  field,
  idx,
  renovationTypeOptions,
  totalLength,
  remove,
}: RenovationTypeProps) => {
  const { t } = useTranslation();

  const coopId = useCooperativeId();

  const {
    clearErrors,
    control,
    formState: { errors },
    register,
    setValue,
    unregister,
    watch,
  } = useFormContext<FormState>();

  const res = useSelector((state) =>
    userSpaceRenovationRequestsApi.endpoints.getUserSpaceRenovationRequestCreateFormOptions.select(
      coopId ?? skipToken
    )(state)
  );

  const portalUserInfo = res.data?.PortalUserInfo ?? null;

  const baseName = useMemo(() => `renovationTypes.${idx}` as const, [idx]);
  const executorType = watch(`renovationTypes.${idx}.executorType`);

  const executorSelectHelpers = useMemo(
    () => getSelectHelpers(executorCodes ?? []),
    [executorCodes]
  );

  const validateDescriptionMaxLength = (value: string) => {
    if (value.length > descriptionsMaxLength)
      return `Maksimimerkkimäärä on ${descriptionsMaxLength} merkkiä. Merkkimäärä on ylittynyt.`;

    return true;
  };

  const validatePattern = (
    value: string | undefined,
    pattern: RegExp,
    message: string
  ) => {
    if (value !== undefined && !pattern.test(value)) return message;

    return true;
  };

  useEffect(() => {
    if (executorType === RenovationExecutor.PortalUser) {
      setValue(
        `${baseName}.portalUserFirstName`,
        portalUserInfo?.FirstName ?? ''
      );
      setValue(
        `${baseName}.portalUserLastName`,
        portalUserInfo?.LastName ?? ''
      );
      setValue(
        `${baseName}.portalUserBirthday`,
        portalUserInfo?.Birthday ? new Date(portalUserInfo.Birthday) : null
      );
      setValue(
        `${baseName}.portalUserEmail`,
        portalUserInfo?.Email?.Email ?? ''
      );
      setValue(
        `${baseName}.portalUserPhone`,
        portalUserInfo?.Phone?.Phone ?? ''
      );
    }
  }, [executorType]);

  useEffect(() => {
    if (executorType === RenovationExecutor.Organization) {
      unregister([
        `${baseName}.foreignRegistrationNumber`,
        `${baseName}.personFirstName`,
        `${baseName}.personLastName`,
        `${baseName}.personBirthday`,
        `${baseName}.personEmail`,
        `${baseName}.personPhone`,
        `${baseName}.portalUserFirstName`,
        `${baseName}.portalUserLastName`,
        `${baseName}.portalUserBirthday`,
        `${baseName}.portalUserEmail`,
        `${baseName}.portalUserPhone`,
      ]);
    } else if (executorType === RenovationExecutor.ForeignCompany) {
      unregister([
        `${baseName}.organizationBusinessId`,
        `${baseName}.personFirstName`,
        `${baseName}.personLastName`,
        `${baseName}.personBirthday`,
        `${baseName}.personEmail`,
        `${baseName}.personPhone`,
        `${baseName}.portalUserFirstName`,
        `${baseName}.portalUserLastName`,
        `${baseName}.portalUserBirthday`,
        `${baseName}.portalUserEmail`,
        `${baseName}.portalUserPhone`,
      ]);
    } else if (executorType === RenovationExecutor.Person) {
      unregister([
        `${baseName}.organizationName`,
        `${baseName}.organizationBusinessId`,
        `${baseName}.foreignRegistrationNumber`,
        `${baseName}.organizationEmail`,
        `${baseName}.organizationPhone`,
        `${baseName}.portalUserFirstName`,
        `${baseName}.portalUserLastName`,
        `${baseName}.portalUserBirthday`,
        `${baseName}.portalUserEmail`,
        `${baseName}.portalUserPhone`,
      ]);
    } else if (executorType === RenovationExecutor.PortalUser) {
      unregister([
        `${baseName}.organizationName`,
        `${baseName}.organizationBusinessId`,
        `${baseName}.foreignRegistrationNumber`,
        `${baseName}.organizationEmail`,
        `${baseName}.organizationPhone`,
        `${baseName}.personFirstName`,
        `${baseName}.personLastName`,
        `${baseName}.personBirthday`,
        `${baseName}.personEmail`,
        `${baseName}.personPhone`,
      ]);
    }
  }, [executorType]);

  return (
    <Stack key={field.id} spacing={2.5}>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={2}
      >
        <Stack
          spacing={1}
          sx={{
            width: {
              md: '80%',
              xs: '100%',
            },
          }}
        >
          <ControlLabel required>Muutostyön tyyppi</ControlLabel>
          <ValidationWarning
            text={errors.renovationTypes?.[idx]?.renovationType?.message}
            handleInitErrors={clearErrors}
            Component={
              <Controller
                control={control}
                name={`renovationTypes.${idx}.renovationType`}
                rules={{
                  required: ValidationWarningMessage.Required,
                }}
                render={({ field: { value, onChange } }) => (
                  <Autocomplete
                    value={value}
                    options={renovationTypeOptions}
                    groupBy={(option) => option.parent}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={(option, value) =>
                      option.value === value.value
                    }
                    onChange={(_, value) => onChange(value)}
                  />
                )}
              />
            }
          />
        </Stack>
        {totalLength > 1 && <Button onClick={() => remove(idx)}>POISTA</Button>}
      </Stack>
      <Stack spacing={1}>
        <ControlLabel required>
          {t('##attribute#incident#uds_descriptionofchange')}
        </ControlLabel>
        <ValidationWarning
          text={errors.renovationTypes?.[idx]?.description?.message}
          Component={
            <Textarea
              spellCheck="false"
              {...register(`renovationTypes.${idx}.description`, {
                required: ValidationWarningMessage.Required,
                validate: {
                  maxLength: validateDescriptionMaxLength,
                },
              })}
              sx={{ minHeight: '200px' }}
            />
          }
          handleInitErrors={clearErrors}
        />
      </Stack>
      <Stack spacing={1} maxWidth={{ xs: '100%', md: '40%' }}>
        <ControlLabel required>Työnsuorittaja</ControlLabel>
        <ValidationWarning
          text={errors.renovationTypes?.[idx]?.executorType?.message}
          handleInitErrors={clearErrors}
          Component={
            <Controller
              control={control}
              name={`renovationTypes.${idx}.executorType`}
              rules={{
                required: ValidationWarningMessage.Required,
              }}
              render={({ field: { value, onChange } }) => (
                <Select
                  value={value ?? undefined}
                  variant="underline"
                  options={executorSelectHelpers.options}
                  getOptionLabel={executorSelectHelpers.getOptionLabel}
                  onChange={onChange}
                />
              )}
            />
          }
        />
      </Stack>

      {renovationExecutorFactory(watch(`renovationTypes.${idx}.executorType`))({
        idx,
        foreignRegistrationNumberMaxLength,
        organizationNameMaxLength,
      })}
    </Stack>
  );
};

export default RenovationType;
